import type { CellChange, Command, FillCellValue, TileCellChange } from "../document/commands.ts";
import { EMPTY_TILE, packTile, packedToTileRef } from "../document/packed-tile.ts";
import type { LayerDocument, WorldDocument } from "../document/types.ts";
import type { ZoneCellChange } from "../document/zones.ts";
import type { DoorCellChange } from "../document/doors.ts";
import { PATCH_PALETTE_MAX, type CellPatchIntent, type PatchGrid, type PatchValue } from "./op-types.ts";
import { layerOf, type Resolution } from "./resolve-types.ts";

/**
 * Resolving a cell patch (ADR-086 decision 19): the cells that do not hold their value yet, with the values
 * they hold, as the cell commands `commands.ts` defines. Cells are compared by meaning: a tile as the tileset
 * and tile it names, packed against this document's own tileset list; a zone cell as the type id its layer's
 * table names; a door cell as the id of the door its layer lists.
 */

/**
 * The most pairs of a value held and a value written that a small patch resolves to fills. Up to this many,
 * each pair is one `fillCells` over one typed array of indices; past it a patch of at most `STROKE_CELLS_MAX`
 * changed cells is a paste of many values and resolves to one stroke command.
 */
const FILL_PAIRS_MAX = 16;

/**
 * The most changed cells a stroke command carries. A stroke holds a record a cell, about 5 ms and a megabyte
 * at this size and 400 ms and 350 MiB at the 4,194,304 cells an operation may cover, so a larger patch
 * resolves to fills, whose cells are typed arrays, whenever its cells share few enough pairs.
 */
export const STROKE_CELLS_MAX = 65_536;

/**
 * The fewest cells a pair must hold on average for a large patch to resolve to fills: with fewer, the fills
 * would be nearly as many as the cells and a stroke is the smaller form.
 */
const FILL_CELLS_PER_PAIR = 4;

/**
 * The most fills a large zone patch resolves to. A zone fill compacts its layer's type table, so each is
 * applied by `applyCommand` in turn, a copy of the grid a fill.
 */
const ZONE_FILLS_MAX = 64;

/**
 * A patch resolved with the document it leaves: its commands, and what applying them in order gives, computed
 * with one copy of the grid.
 */
export interface AppliedPatch {
  readonly commands: readonly Command[];
  readonly document: WorldDocument;
}

/**
 * The grid of a layer that a patch of a kind writes.
 *
 * @param layer The layer.
 * @param grid The patch's grid.
 * @returns The grid, or undefined when the layer holds no grid of that kind.
 */
function heldGrid(layer: LayerDocument, grid: PatchGrid): Readonly<Uint8Array> | Readonly<Uint32Array> | undefined {
  switch (grid) {
    case "corners":
      return layer.kind === "terrain" ? layer.corners : undefined;
    case "collision":
      return layer.kind === "collision" ? layer.cells : undefined;
    case "zone":
      return layer.kind === "zone" ? layer.cells : undefined;
    case "tiles":
      return layer.kind === "tile" ? layer.tiles : undefined;
    case "door":
      return layer.kind === "door" ? layer.cells : undefined;
  }
}

/**
 * The number a grid holds for a palette value, to compare a cell with: the byte itself, the packed tile, or a
 * zone type's or a door's place in the layer's table.
 *
 * @param value The palette value.
 * @param layer The layer.
 * @param world The document, for its tileset list.
 * @returns The number; -1 for a zone type or a door the layer's table lacks, which no cell holds; or undefined for
 *   a tile of a tileset the world does not list.
 */
function codeOf(value: PatchValue, layer: LayerDocument, world: WorldDocument): number | undefined {
  if (typeof value === "number") {
    return value;
  }
  if (layer.kind === "zone") {
    return value === null ? 0 : typeof value === "string" ? layer.types.indexOf(value) + 1 || -1 : -1;
  }
  if (layer.kind === "door") {
    return value === null ? 0 : typeof value === "string" ? layer.doors.findIndex((door) => door.id === value) + 1 || -1 : -1;
  }
  if (value === null || typeof value === "string") {
    return EMPTY_TILE;
  }
  const index = world.tilesets.findIndex((tileset) => tileset.id === value.tileset);
  return index < 0 ? undefined : packTile(index, value.tile, value);
}

/**
 * What a cell's number means: the value a command carries as its before-value.
 *
 * @param code The number the grid holds.
 * @param layer The layer.
 * @param world The document, for its tileset list.
 * @returns The byte, the zone type's or the door's id or null, or the tile reference or null.
 */
function meaningOf(code: number, layer: LayerDocument, world: WorldDocument): FillCellValue {
  if (layer.kind === "zone") {
    return code === 0 ? null : (layer.types[code - 1] ?? null);
  }
  if (layer.kind === "door") {
    return code === 0 ? null : (layer.doors[code - 1]?.id ?? null);
  }
  return layer.kind === "tile" ? packedToTileRef(code, world.tilesets) : code;
}

/**
 * The stroke command of a grid's kind over the given cells.
 *
 * @param intent The patch.
 * @param cells Each cell's index, the value it holds and the value it takes.
 * @returns The command.
 */
function strokeOf(intent: CellPatchIntent, cells: readonly { readonly index: number; readonly before: FillCellValue; readonly after: FillCellValue }[]): Command {
  const at = { mapId: intent.map, layerId: intent.layer };
  switch (intent.grid) {
    case "corners":
      return { kind: "paintCorners", ...at, cells: cells as readonly CellChange[] };
    case "collision":
      return { kind: "paintCollision", ...at, cells: cells as readonly CellChange[] };
    case "zone":
      return { kind: "paintZone", ...at, cells: cells as readonly ZoneCellChange[] };
    case "tiles":
      return { kind: "setTiles", ...at, cells: cells as readonly TileCellChange[] };
    case "door":
      return { kind: "paintDoor", ...at, cells: cells as readonly DoorCellChange[] };
  }
}

/**
 * The document with a layer's grid replaced.
 *
 * @param world The document.
 * @param mapId The map.
 * @param layer The layer as held.
 * @param grid The new grid, of the layer's own kind.
 * @returns The new document, sharing every other layer.
 */
function withGrid(world: WorldDocument, mapId: string, layer: LayerDocument, grid: Uint8Array | Uint32Array): WorldDocument {
  const replaced = (layer.kind === "terrain" ? { ...layer, corners: grid } : layer.kind === "tile" ? { ...layer, tiles: grid } : { ...layer, cells: grid }) as LayerDocument;
  return { ...world, maps: world.maps.map((map) => (map.id === mapId ? { ...map, layers: map.layers.map((other) => (other === layer ? replaced : other)) } : map)) };
}

/**
 * Resolves a cell patch against a document.
 *
 * @param world The document.
 * @param intent The patch.
 * @returns The commands that write the cells not yet holding their value, none when every cell holds its
 *   value, or the reason the patch is skipped: the map or the layer is gone, the layer holds no grid of the
 *   patch's kind, a cell lies outside the grid, a tile names a tileset the world lacks, a door cell names a door
 *   its layer does not list (`gone`), or (`refused`) a byte is not an integer from 0 to 255, which the
 *   codec never lets through. The commands are one
 *   `fillCells` for each pair of a value held and a value written, in the order the cells first meet them,
 *   when there are at most `FILL_PAIRS_MAX` pairs, or when more than `STROKE_CELLS_MAX` cells change and the
 *   pairs hold `FILL_CELLS_PER_PAIR` cells each on average (at most `ZONE_FILLS_MAX` pairs for zone cells);
 *   otherwise one stroke command. Fills over bytes and tiles come with the document they leave.
 */
export function resolveCells(world: WorldDocument, intent: CellPatchIntent): Resolution | AppliedPatch {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  const held = heldGrid(layer, intent.grid);
  if (held === undefined) {
    return "kind";
  }
  const last = intent.cells[intent.cells.length - 1] as readonly [number, number];
  if (last[0] + last[1] > held.length) {
    return "range";
  }
  const codes: number[] = [];
  for (const value of intent.palette) {
    const code = codeOf(value, layer, world);
    if (code === undefined) {
      return "tileset";
    }
    if (code === -1 && layer.kind === "door") {
      return "gone";
    }
    if (held instanceof Uint8Array && layer.kind !== "zone" && !(Number.isInteger(code) && code >= 0 && code <= 255)) {
      return "refused";
    }
    codes.push(code);
  }
  let covered = 0;
  for (const run of intent.cells) {
    covered += run[1];
  }
  const indices = new Uint32Array(covered);
  const befores = new Uint32Array(covered);
  const written = new Uint16Array(covered);
  const pairs = new Map<number, number>();
  let changed = 0;
  let run = 0;
  let [entry, left] = intent.values[0] as readonly [number, number];
  let code = codes[entry] as number;
  let pair = -1;
  let count = 0;
  for (const [start, length] of intent.cells) {
    for (let index = start; index < start + length; index += 1) {
      if (left === 0) {
        run += 1;
        [entry, left] = intent.values[run] as readonly [number, number];
        code = codes[entry] as number;
      }
      left -= 1;
      const before = held[index] as number;
      if (before !== code) {
        indices[changed] = index;
        befores[changed] = before;
        written[changed] = entry;
        changed += 1;
        const key = before * PATCH_PALETTE_MAX + entry;
        if (key !== pair) {
          if (count > 0) {
            pairs.set(pair, (pairs.get(pair) ?? 0) + count);
          }
          pair = key;
          count = 0;
        }
        count += 1;
      }
    }
  }
  if (changed === 0) {
    return [];
  }
  pairs.set(pair, (pairs.get(pair) ?? 0) + count);
  const most = changed > STROKE_CELLS_MAX ? Math.max(FILL_PAIRS_MAX, layer.kind === "zone" ? ZONE_FILLS_MAX : Math.floor(changed / FILL_CELLS_PER_PAIR)) : FILL_PAIRS_MAX;
  if (pairs.size > most) {
    return [strokeOf(intent, Array.from({ length: changed }, (_, i) => ({ index: indices[i] as number, before: meaningOf(befores[i] as number, layer, world), after: intent.palette[written[i] as number] as FillCellValue })))];
  }
  const fills = new Map<number, { readonly cells: Uint32Array; next: number }>();
  for (const [key, size] of pairs) {
    fills.set(key, { cells: new Uint32Array(size), next: 0 });
  }
  let fill = fills.get(pair) as { readonly cells: Uint32Array; next: number };
  for (let i = 0; i < changed; i += 1) {
    const key = (befores[i] as number) * PATCH_PALETTE_MAX + (written[i] as number);
    if (key !== pair) {
      pair = key;
      fill = fills.get(key) as { readonly cells: Uint32Array; next: number };
    }
    fill.cells[fill.next] = indices[i] as number;
    fill.next += 1;
  }
  const commands: Command[] = [];
  for (const [key, { cells }] of fills) {
    commands.push({ kind: "fillCells", mapId: intent.map, layerId: intent.layer, indices: cells, before: meaningOf(Math.floor(key / PATCH_PALETTE_MAX), layer, world), after: intent.palette[key % PATCH_PALETTE_MAX] as FillCellValue });
  }
  if (layer.kind === "zone") {
    return commands;
  }
  const grid = held.slice();
  for (let i = 0; i < changed; i += 1) {
    grid[indices[i] as number] = codes[written[i] as number] as number;
  }
  return { commands, document: withGrid(world, intent.map, layer, grid) };
}
